/**
 * model-orchestration-contracts.ts
 *
 * Sprint MESH-ORCH-01 (v5): Supply-Chain Gated Multi-Model Node Orchestration,
 * Independent Needle2 Elastic Worker Pools, Single-Instance LLM Runtimes,
 * Monotonic Generation Identity, Versioned Storage, and Fault Domain Isolation.
 */

export type ModelKind = 'needle2' | 'llm';

export type NodeLifecycleState =
  | 'DISCOVERED'
  | 'REGISTERED'
  | 'ONLINE'
  | 'DEGRADED'
  | 'DRAINING'
  | 'OFFLINE'
  | 'RECOVERING';

export type MetadataTrust = 'trusted' | 'verified' | 'locally-overridden';
export type ModelStatus = 'available' | 'invalid' | 'missing' | 'uninstalled';

// ── Model Catalog Definition ───────────────────────────────────────────────────

export interface ModelDefinition {
  readonly id: string;
  readonly name: string;
  readonly kind: ModelKind;
  readonly supportsMultiInstance: boolean;
  readonly format: 'gguf' | 'other';
  readonly filePath: string;
  readonly fileSizeBytes: number;
  readonly sha256: string;
  readonly estimatedRamMb: number;
  readonly estimatedVramMb: number;
  readonly contextLength?: number;
  readonly quantization?: string;
  readonly capabilities: readonly string[];
  readonly source?: string;
  readonly metadataTrust: MetadataTrust;
  readonly status: ModelStatus;
  readonly createdAt: string;
}

// ── Runtime Records ────────────────────────────────────────────────────────────

export type DesiredRuntimeState = 'stopped' | 'running' | 'draining';
export type RuntimeStatus = 'starting' | 'active' | 'draining' | 'stopped' | 'failed';

export interface ModelRuntime {
  /** Formula: `${nodeId}:${kind}:${modelId}:i${instanceIndex}:g${generation}` */
  readonly runtimeId: string;
  readonly nodeId: string;
  readonly modelId: string;
  readonly kind: ModelKind;
  readonly instanceIndex: number;
  readonly generation: number;

  readonly desiredState: DesiredRuntimeState;
  readonly status: RuntimeStatus;

  readonly allocatedRamMb: number;
  readonly allocatedVramMb: number;
  readonly maxConcurrentRequests: number;
  readonly activeRequests: number;

  readonly startedAt?: string;
  readonly lastActiveAt?: string;
  readonly lastHealthCheckAt?: string;
  readonly processId?: number;
  readonly endpoint?: string;
  readonly error?: string;

  readonly protectedWorkload: boolean;
  readonly drainTimeoutMs: number;
  readonly revision: number;
}

// ── Worker Index Allocation ────────────────────────────────────────────────────

export interface WorkerIndexAllocator {
  readonly nodeId: string;
  readonly modelId: string;
  readonly nextIndex: number;
  readonly generation: number;
}

// ── Runtime Intent ─────────────────────────────────────────────────────────────

export interface RuntimeIntent {
  readonly nodeId: string;
  readonly modelId: string;
  readonly kind: ModelKind;
  readonly desiredInstances: number;
  readonly enabled: boolean;
  readonly updatedAt: string;
  readonly updatedByActorId: string;
}

// ── Node Capacity & Policy ─────────────────────────────────────────────────────

export interface RecoveryRampPhase {
  readonly durationMs: number | 'infinite';
  readonly capacityPercent: number; // 1-100
}

export interface NodeRuntimePolicy {
  readonly disabled: boolean;
  readonly quarantined: boolean;
  readonly draining: boolean;
  readonly allowMasterFallback: boolean;
  readonly maxNeedleInstances: number;
  readonly maxActiveLlmRuntimes: number;
  readonly allowMixedLlmModels: boolean;
  readonly recoveryRampUpPhases: readonly RecoveryRampPhase[];
}

export interface NodeCapacityMetrics {
  readonly totalCpuCores: number;
  readonly availableCpuCores: number;
  readonly totalMemoryMb: number;
  readonly availableMemoryMb: number;
  readonly gpuName?: string;
  readonly vramTotalMb?: number;
  readonly vramAvailableMb?: number;
  readonly maxNeedleInstances: number;
  readonly activeNeedleInstances: number;
  readonly maxLlmInstances: number;
  readonly activeLlmInstances: number;
}

export interface NodeCapability {
  readonly nodeId: string;
  readonly nodeName: string;
  readonly roles: readonly ('master' | 'helper')[];
  readonly supportedModelKinds: readonly ModelKind[];
  readonly installedModelIds: readonly string[];
  readonly capacity: NodeCapacityMetrics;
  readonly policy: NodeRuntimePolicy;
  readonly lastHeartbeatAt: string;
  readonly lifecycleState: NodeLifecycleState;
  readonly recoveryStartedAt?: string;
}

// ── Heartbeat ──────────────────────────────────────────────────────────────────

export interface NodeHeartbeat {
  readonly nodeId: string;
  readonly observedAt: string;
  readonly lifecycleState: NodeLifecycleState;
  readonly capacity: NodeCapacityMetrics;
  readonly activeRuntimeIds: readonly string[];
  readonly consecutiveHealthyCount: number;
}

export interface HeartbeatPolicy {
  readonly intervalMs: number;
  readonly degradedAfterMissed: number;
  readonly offlineAfterMissed: number;
  readonly recoveryConfirmationCount: number;
}

// ── Assignments ────────────────────────────────────────────────────────────────

export type AssignmentScope =
  | 'global'
  | 'node'
  | 'service'
  | 'agent'
  | 'channel'
  | 'tenant';

export type FailoverMode = 'next-best' | 'master-fallback' | 'downgrade' | 'reject';

export interface ScalingPolicy {
  readonly scaleUpUtilizationPercent: number;
  readonly scaleDownUtilizationPercent: number;
  readonly scaleUpWindowMs: number;
  readonly scaleDownWindowMs: number;
  readonly cooldownMs: number;
  readonly maxScaleStep: number;
}

export interface ModelAssignment {
  readonly id: string;
  readonly scope: AssignmentScope;
  readonly scopeId: string;
  readonly modelKind: ModelKind;
  readonly preferredModelId?: string;
  readonly preferredNodeId?: string;
  readonly fallbackModelIds: readonly string[];
  readonly fallbackNodeIds: readonly string[];
  readonly minReplicas?: number;
  readonly maxReplicas?: number;
  readonly priority: number;
  readonly requiredCapabilities: readonly string[];
  readonly maxLatencyMs?: number;
  readonly scalingPolicyOverride?: Partial<ScalingPolicy>;
  readonly failoverMode: FailoverMode;
  readonly allowMasterFallback: boolean;
  readonly allowCrossModelFallback: boolean;
  readonly enabled: boolean;
}

// ── Routing Decisions ──────────────────────────────────────────────────────────

export interface CandidateRejection {
  readonly nodeId: string;
  readonly runtimeId?: string;
  readonly reason: string;
}

export interface RoutingDecision {
  readonly requestId: string;
  readonly modelKind: ModelKind;
  readonly selectedNodeId: string;
  readonly selectedRuntimeId: string;
  readonly instanceIndex: number;
  readonly score: number;
  readonly reasons: readonly string[];
  readonly rejectionReasons: readonly CandidateRejection[];
  readonly fallbackCandidates: readonly {
    readonly nodeId: string;
    readonly runtimeId: string;
    readonly score: number;
  }[];
  readonly decidedAt: string;
}

// ── Scale Signal ───────────────────────────────────────────────────────────────

export interface ScaleSignal {
  readonly signalId: string;
  readonly nodeId: string;
  readonly modelId: string;
  readonly currentInstances: number;
  readonly recommendedInstances: number;
  readonly utilizationPercent: number;
  readonly signal: 'SCALE_UP_RECOMMENDED' | 'SCALE_DOWN_RECOMMENDED';
  readonly blocked?: string;
  readonly approvedAt?: string;
  readonly rejectedAt?: string;
  readonly issuedAt: string;
}

// ── Request Lifecycle ──────────────────────────────────────────────────────────

export type RequestState =
  | 'queued'
  | 'assigned'
  | 'running'
  | 'completed'
  | 'retrying'
  | 'failed';

export interface ManagedRequest {
  readonly requestId: string;
  readonly idempotencyKey: string;
  readonly idempotencyKeyExpiresAt: string;
  readonly isIdempotent: boolean;
  readonly modelKind: ModelKind;
  readonly assignedRuntimeId?: string;
  readonly state: RequestState;
  readonly retryCount: number;
  readonly maxRetries: number;
  readonly enqueuedAt: string;
  readonly assignedAt?: string;
  readonly completedAt?: string;
  readonly error?: string;
}

// ── Events ─────────────────────────────────────────────────────────────────────

export type OrchestrationEventKind =
  | 'runtime.started'
  | 'runtime.stopped'
  | 'runtime.failed'
  | 'runtime.scaled'
  | 'runtime.drain_timeout'
  | 'runtime.force_killed'
  | 'node.online'
  | 'node.degraded'
  | 'node.draining'
  | 'node.offline'
  | 'node.recovered'
  | 'node.rampup.started'
  | 'node.rampup.throttled'
  | 'node.rampup.completed'
  | 'assignment.created'
  | 'assignment.updated'
  | 'assignment.deleted'
  | 'failover.triggered'
  | 'failover.completed'
  | 'scale.signal.emitted'
  | 'scale.signal.approved'
  | 'scale.signal.rejected'
  | 'model.imported'
  | 'model.import_failed'
  | 'model.validated'
  | 'model.validation_failed'
  | 'model.uninstalled'
  | 'request.queued'
  | 'request.assigned'
  | 'request.completed'
  | 'request.retried'
  | 'request.retry_requires_ack'
  | 'request.drain_orphaned'
  | 'request.failed'
  | 'request.backpressure_rejected';

export interface OrchestrationEvent {
  readonly eventId: string;
  readonly kind: OrchestrationEventKind;
  readonly payload: Record<string, unknown>;
  readonly actorId?: string;
  readonly originId: string;
  readonly sequence: number;
  readonly correlationId?: string;
  readonly occurredAt: string;
}

// ── API Envelopes ──────────────────────────────────────────────────────────────

export interface MutationResponse<T> {
  readonly data: T;
  readonly eventId: string;
  readonly revision: number;
  readonly idempotent: boolean;
}

// ── Common Domain Errors ───────────────────────────────────────────────────────

export class RevisionConflictError extends Error {
  constructor(public expectedRevision: number, public actualRevision: number) {
    super(`CAS conflict: expected revision ${expectedRevision}, found ${actualRevision}`);
    this.name = 'RevisionConflictError';
  }
}

export class PoolRevisionConflictError extends Error {
  constructor(public nodeId: string, public modelId: string) {
    super(`Pool CAS conflict: concurrent scale operation on ${nodeId}/${modelId}`);
    this.name = 'PoolRevisionConflictError';
  }
}

export class ModelInUseError extends Error {
  constructor(public modelId: string, public activeRuntimeCount: number) {
    super(`Cannot uninstall model ${modelId}: ${activeRuntimeCount} active runtimes`);
    this.name = 'ModelInUseError';
  }
}

export class ModelAssignedError extends Error {
  constructor(public modelId: string, public assignmentCount: number) {
    super(`Cannot uninstall model ${modelId}: mapped in ${assignmentCount} assignments`);
    this.name = 'ModelAssignedError';
  }
}

export class DuplicateModelError extends Error {
  constructor(public sha256: string, public existingModelId: string) {
    super(`Model with SHA-256 digest ${sha256} already exists (${existingModelId})`);
    this.name = 'DuplicateModelError';
  }
}

export class CapacityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CapacityError';
  }
}

export class PolicyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PolicyError';
  }
}

export class QueueFullError extends Error {
  constructor(message: string = 'Request queue is full (maxQueueDepth exceeded)') {
    super(message);
    this.name = 'QueueFullError';
  }
}

export class ServiceUnavailableError extends Error {
  constructor(message: string = 'No eligible runtime available within queueTimeoutMs') {
    super(message);
    this.name = 'ServiceUnavailableError';
  }
}

export class AuthorizationError extends Error {
  constructor(message: string = 'Action requires elevated administrative authorization') {
    super(message);
    this.name = 'AuthorizationError';
  }
}
